import {
  CircularProgress,
  Dialog,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextareaAutosize,
} from "@mui/material";
import noImage from "assets/images/no-image.png";
import Input from "components/common/Input";
import Select from "components/common/Select";
import api from "config/api";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { setCartBtnLoading, setSelected } from "store/slices/main.slice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CrustType, SizeType } from "utils/types";
import AddIcon from "@mui/icons-material/Add";
import Button from "components/common/Button";
import RemoveIcon from "@mui/icons-material/Remove";
import ModificationPopUp from "./ModificationPopUp";
import VariantSelection from "components/common/VariantSelection";
import { toast } from "react-toastify";
import { addUpdateToCart } from "store/action/main";

type Props = {};

const initialConfig = {
  qty: 1,
  size: 0,
  crust: 0,
  note: "",
  id: "",
  pizzaName: "",
  spiceLevel: "",
  modifications: undefined,
  variant: [],
};

const ProductPopUp = (props: Props) => {
  const dispatch = useAppDispatch();

  const [selectedProduct, setSelectedProduct] = useState<any>(undefined);
  const [variants, setVariants] = useState<any>([]);
  const [sizes, setSizes] = useState<SizeType[]>([]);
  const [crusts, setCrusts] = useState<CrustType[]>([]);
  const [showModification, setshowModification] = useState<any>(undefined);
  const [selectedVariantItem, setSelectedVariantItem] = useState({});
  const [config, setConfig] = useState(initialConfig);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentPizzaIndex, setCurrentPizzaIndex] = useState(0);
  const [pizzasInDeal, setPizzasInDeal] = useState<any[]>([]);
  const [spiceData, setSpiceData] = useState<any[]>([]);
  const [checkCartConfigData, setCheckCartConfigData] = useState<number[]>([]);

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // Product image click -> open image preview popup
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // FIX (All devices): Prevent "The object can not be cloned." on 2nd edit.
  // structuredClone sometimes fails when state contains non-cloneable values.
  // This safeClone supports Set/Map/Date and removes functions safely.
  const safeClone = (obj: any) => {
    try {
      return structuredClone(obj);
    } catch (_err) {
      const seen = new WeakMap<object, any>();

      const cloneRec = (v: any): any => {
        if (v === null || typeof v !== "object") return v;

        // structuredClone fails on functions
        if (typeof v === "function") return undefined;

        if (v instanceof Date) return new Date(v.getTime());

        if (v instanceof Set) {
          const out = new Set<any>();
          v.forEach((item) => out.add(cloneRec(item)));
          return out;
        }

        if (v instanceof Map) {
          const out = new Map<any, any>();
          v.forEach((val, key) => out.set(cloneRec(key), cloneRec(val)));
          return out;
        }

        if (Array.isArray(v)) return v.map(cloneRec);

        if (seen.has(v)) return seen.get(v);

        const out: any = {};
        seen.set(v, out);

        Object.keys(v).forEach((k) => {
          const cv = cloneRec(v[k]);
          if (cv !== undefined) out[k] = cv;
        });

        return out;
      };

      return cloneRec(obj);
    }
  };
  // === END SAMPATH ===

  const product = useAppSelector(({ main }) => main.selectedProduct);
  const { settings, deliveryType, cartBtnLoading, taxes, pizzaList } =
    useAppSelector(({ main }) => main);

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // Safe helper for UI price display (design only)
  const formatMoney = (v: any) => {
    const n = Number(v);
    if (Number.isNaN(n)) return "$0.00";
    return `$${n.toFixed(2)}`;
  };
  // === END SAMPATH ===

  const allHavePizzaName = useMemo(
    // === ADDED BY SAMPATH – 2026-01-05 ===
    // Prevent crash if pizzaName is undefined (UI stability)
    () => pizzasInDeal.every((item) => (item.pizzaName || "").length > 0),
    // === END SAMPATH ===
    [pizzasInDeal]
  );

  const variantStep = useMemo(() => {
    if (!variants?.[currentStep]) {
      return { data: [], mandatoryLevel: 0, qtyLevel: 0 };
    }

    return variants[currentStep];
  }, [variants, currentStep]);

  const allVariants = useMemo(
    () =>
      variants.reduce((obj: any, { data = [] }: any) => {
        data.forEach((element: any) => {
          obj[element.id] = element;
          element?.subLevelData?.forEach((subElement: any) => {
            obj[subElement.id] = subElement;
          });
        });
        return obj;
      }, {}),
    [variants]
  );

  const steps = useMemo(() => {
    if (!selectedProduct || selectedProduct?.isPizza) return [];
    const keys = variants.map((_: any, i: number) => i + 1);
    return [...keys, keys.length + 1];
  }, [selectedProduct, variants]);

  const totalModifications = useMemo(
    () => Object.values(config?.modifications || {}).flat().length,
    [config]
  );

  useEffect(() => {
    const id = product?.productId;
    if (id && product.isPizza) {
      api
        .get("/api/Modifier/PizzaSubLevels?productId=" + id)
        .then(({ data }) => {
          setSpiceData(data);
        })
        .catch((e) => {});
    }
  }, [product]);

  useEffect(() => {
    return () => {
      dispatch(setCartBtnLoading(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!product?.data) return;

    if (
      (product.data.isDeal && product.data.isPizza) ||
      product.data.pizzasInDeal > 0
    ) {
      const cartonfig =
        pizzasInDeal?.length > 0
          ? {
              ...pizzasInDeal?.[currentPizzaIndex],
            }
          : {
              ...product.data?.selectedDealPizzas?.[currentPizzaIndex],
            };

      const indexFind = checkCartConfigData.findIndex(
        (e) => e === currentPizzaIndex
      );

      if (indexFind === -1) {
        setConfig({ ...cartonfig?.config });
        setCheckCartConfigData((e) => [...e, currentPizzaIndex]);
      }
    }

    if (product.data.config.variant) {
      setSelectedVariantItem(product.data.config.variant?.[0] || {});
    }
  }, [checkCartConfigData, currentPizzaIndex, pizzasInDeal, product?.data]);

  useEffect(() => {
    if (product?.data) {
      setSelectedProduct(product.data);

      if (
        (product.data.isDeal && product.data.isPizza) ||
        product.data.pizzasInDeal > 0
      ) {
        setPizzasInDeal([...product.data?.selectedDealPizzas]);
      } else {
        setCheckCartConfigData([0]);
        setConfig(product.data.config);
      }
    } else {
      setSelectedProduct(product);
      if (
        (product?.isDeal && product?.isPizza) ||
        (product?.pizzasInDeal && product?.pizzasInDeal > 0)
      ) {
        const initialPizzas = Array(product.pizzasInDeal).fill({
          ...initialConfig,
        });
        setPizzasInDeal(initialPizzas);
        setConfig(initialPizzas[0]);
      }
    }
  }, [product]);

  useEffect(() => {
    if (selectedProduct) {
      if (selectedProduct.isPizza) {
        api
          .get(
            `/api/${
              selectedProduct.isCreateYourOwn
                ? "PizzaToppingPrice"
                : "PizzaSize/ByProductId"
            }?productId=${selectedProduct.productId}`
          )
          .then(({ data }) => {
            if (!product?.isEdit) {
              setConfig((e) => ({ ...e, size: data[0].sizeId }));
            }
            setSizes(data);
          })
          .catch(() => {});
      } else {
        api
          .get(
            `/api/Variation/VariantById?productId=${selectedProduct.productId}`
          )
          .then(({ data }) => setVariants(data))
          .catch(() => {});
      }
    }
  }, [selectedProduct, product]);

  useEffect(() => {
    if (selectedProduct && selectedProduct.isPizza && config.size) {
      api
        .get(
          `/api/Modifier/Crusts?sizeId=${config.size}&crustId=0&productId=${selectedProduct.productId}`
        )
        .then(({ data }) => {
          if (!product?.isEdit) {
            setConfig((e) => ({ ...e, crust: data[0].modifierId }));
          }
          setCrusts(data);
        })
        .catch(toast.error);
    }
  }, [config.size, selectedProduct, product]);

  const addItemToCart = () => {
    // (NO CHANGES INSIDE - keep your working logic)
    const updatedPizzasInDeal = [...pizzasInDeal];
    updatedPizzasInDeal[currentPizzaIndex] = config;
    toast.dismiss();
    const [tax1 = { rate: 0 }, tax2 = { rate: 0 }, tax3 = { rate: 0 }] =
      taxes ?? [];
    const pizza = pizzaList.find((i) => i.productId === config.id);
    const size = sizes.find((i) => i.sizeId === config.size);
    const crust = crusts.find((i) => i.modifierId === config.crust);

    if (!selectedProduct) return;

    const isDeal = selectedProduct.isDeal;
    const isPizza = selectedProduct.isPizza;
    const isCreateYourOwn = selectedProduct.isCreateYourOwn;
    const isPizzasInDeal = selectedProduct.pizzasInDeal > 0;

    const basePrice =
      settings.ApplyTakeOutDeliveryPrice !== 0
        ? deliveryType !== "pickup"
          ? selectedProduct?.deliveryprice || selectedProduct.price
          : selectedProduct?.takeoutprice || selectedProduct.price
        : !!selectedProduct.isDeal || !!selectedProduct.isCreateYourOwn
        ? 0
        : selectedProduct.price;

    const finalProduct: any = {
      data: {},
      id: selectedProduct.productId,
      productId: selectedProduct.productId,
      productCode: selectedProduct.productCode,
      fullDescription: selectedProduct.productName,
      quantity: config.qty,
      price: basePrice,
      variationsPrice: 0,
      totalUnitPrice: basePrice,
      subTotal: basePrice * config.qty,
      itemTotal: 0,
      taxId1: selectedProduct.taxId1,
      taxId2: selectedProduct.taxId2,
      taxId3: selectedProduct.taxId3,
      note: config.note,
      totalTax1: 0,
      totalTax2: 0,
      totalTax3: 0,
      status: "new",
      modifiers: [],
      isDeal,
      isPizza,
      isCreateYourOwn,
      taxRate1: tax1?.rate ?? 0,
      taxRate2: tax2?.rate ?? 0,
      taxRate3: tax3?.rate ?? 0,
    };

    if (product?.data) {
      finalProduct.data = {
        ...product.data,
        config,
        isEdit: true,
        index: product.index,
      };
    } else {
      finalProduct.data = { ...product, config };
    }

    if (isPizza || isDeal || isCreateYourOwn) {
      if (!size || !crust) {
        toast.error("Size and Crust are required");
        return;
      }
      finalProduct.pizzaCrust = {
        crustId: crust.modifierId,
        crustName: crust.modifierName,
        crustPrice: crust.modifierCost,
      };

      if (crust.modifierCost) {
        finalProduct.itemTotal += crust.modifierCost;
      }

      finalProduct.pizzaSize = {
        pizzasizeId: size.sizeId,
        size: size.sizeName,
      };
    }
    if (isDeal || isPizza) {
      finalProduct.spiceLevel = {
        spiceId: 1,
        spicename: config.spiceLevel,
      };
    }

    if (isDeal || isPizzasInDeal) {
      finalProduct.data["selectedDealPizzas"] = updatedPizzasInDeal.map(
        (pizzaConfig) => {
          const pizzaSize = sizes.find((s) => s.sizeId === pizzaConfig.size);
          const foundSpice = spiceData.find(
            (el) => el.modifierName === pizzaConfig?.spiceLevel
          );

          const subTotal = +(getSizePrice(pizzaSize) || 0);
          const dealPizza = {
            id: pizzaConfig?.id,
            crust: pizzaConfig.crust,
            config: pizzaConfig, // old config
            productId: selectedProduct.productId,
            productCode: selectedProduct.productCode,
            fullDescription: selectedProduct.productName,
            quantity: 1,
            price: subTotal,
            variationsPrice: 0,
            totalUnitPrice: selectedProduct.price,
            subTotal,
            itemTotal: subTotal,
            taxId1: selectedProduct.taxId1,
            taxId2: selectedProduct.taxId2,
            taxId3: selectedProduct.taxId3,
            note: pizzaConfig.note,
            totalTax1: 0,
            totalTax2: 0,
            totalTax3: 0,
            status: "new",
            modifiers: pizzaConfig.modifications
              ? Object.values(pizzaConfig.modifications)
                  .flat()
                  .map((item: any) => ({
                    defaultTopping: item.defaultTopping,
                    newDefaultTopping: item.newDefaultTopping,
                    modifierId: item.pizzaModifierId,
                    description: item.pizzaModifierName,
                    quantity: 1,
                    unitPrice: item.pizzaModifierPrice,
                    subTotal: item.pizzaModifierPrice,
                    totalValue: item.pizzaModifierPrice,
                    note: "",
                    isFree:
                      item.defaultTopping || item.newDefaultTopping ? 0 : 0,
                    pizzaRegion: 1,
                  }))
              : [],
            isDeal: 1,
            isPizza: 1,
            isCreateYourOwn: 0,
            taxRate1: tax1?.rate ?? 0,
            taxRate2: tax2?.rate ?? 0,
            taxRate3: tax3?.rate ?? 0,
            spiceLevel: {
              spiceId: foundSpice?.modifierId || 1,
              spicename: foundSpice?.modifierName || config?.spiceLevel,
            },
            pizzaSize: {
              pizzasizeId: pizzaSize?.sizeId,
              size: pizzaSize?.sizeName || config.size,
            },
            pizzaName: pizzaConfig.pizzaName ?? selectedProduct?.productName,
          };

          if (selectedProduct.taxId1 === 1 && tax1?.rate) {
            const taxValue = +(dealPizza.subTotal * (tax1?.rate / 100)).toFixed(
              5
            );
            dealPizza.totalTax1 = taxValue;
            dealPizza.itemTotal += taxValue;
          }
          if (selectedProduct.taxId2 === 2 && tax2?.rate) {
            const taxValue = +(dealPizza.subTotal * (tax2?.rate / 100)).toFixed(
              5
            );
            dealPizza.totalTax2 = taxValue;
            dealPizza.itemTotal += taxValue;
          }
          if (selectedProduct.taxId3 === 3 && tax3?.rate) {
            const taxValue = +(dealPizza.subTotal * (tax3?.rate / 100)).toFixed(
              5
            );
            dealPizza.totalTax3 = taxValue;
            dealPizza.totalUnitPrice = dealPizza.itemTotal;
            dealPizza.itemTotal += taxValue;
          }

          if (!!config?.modifications) {
            const [toppingTotal]: any = Object.values(
              config?.modifications
            ).filter((fil: any) => fil.length > 0);

            finalProduct.subTotal += toppingTotal?.reduce(
              (sum: number, item: any, index: number) =>
                index >= selectedProduct.toppingsEachPizza
                  ? sum + item.pizzaModifierPrice
                  : sum + 0,
              0
            );
          }

          finalProduct.subTotal += subTotal * config.qty;
          return dealPizza;
        }
      );

      finalProduct.itemTotal = finalProduct.subTotal;
      finalProduct.totalTax1 = finalProduct.data.selectedDealPizzas.reduce(
        (sum: number, pizza: any) => sum + pizza.totalTax1,
        0
      );
      finalProduct.totalTax2 = finalProduct.data.selectedDealPizzas.reduce(
        (sum: number, pizza: any) => sum + pizza.totalTax2,
        0
      );
      finalProduct.totalTax3 = finalProduct.data.selectedDealPizzas.reduce(
        (sum: number, pizza: any) => sum + pizza.totalTax3,
        0
      );

      finalProduct.itemTotal +=
        finalProduct.totalTax1 +
        finalProduct.totalTax2 +
        finalProduct.totalTax3;
    } else {
      const [baseAMt] = Object.entries(size || {}).reduce(
        ([tk, tv], [key, value]: any) => {
          if (!key.includes("toppingPrice") || !value) return [tk, tv];
          const v = +key.replace("toppingPrice", "");
          if (tk < v) return [v, value];
          return [tk, tv];
        },
        [0, 0]
      );

      if (config?.modifications) {
        finalProduct.modifiers = Object.values(config?.modifications || {})
          .flat()
          .map((item: any, index: number) => ({
            defaultTopping: item.defaultTopping,
            newDefaultTopping: item.newDefaultTopping,
            modifierId: item.pizzaModifierId,
            description: item.pizzaModifierName,
            quantity: 1,
            unitPrice: item.pizzaModifierPrice,
            subTotal: item.pizzaModifierPrice,
            totalValue: item.pizzaModifierPrice,
            note: "",
            isFree: index < baseAMt ? 1 : 0,
            pizzaRegion: 1,
          }));

        const toppingPrice =
          Object.values(config?.modifications || {})
            .flat()
            .filter((i: any) => !i.defaultTopping && !i.newDefaultTopping)
            .slice(baseAMt)
            .reduce((t: number, i: any) => (t += i.pizzaModifierPrice), 0) || 0;

        finalProduct.itemTotal += toppingPrice;
      } else {
        finalProduct.modifiers = selectedProduct.defaultPizzaToppings.map(
          (item: any, index: number) => ({
            defaultTopping: 1,
            newDefaultTopping: 0,
            modifierId: item.modifierId,
            description: item.modifierName,
            quantity: 1,
            unitPrice: 0,
            subTotal: 0,
            totalValue: 0,
            note: "",
            isFree: 1,
            pizzaRegion: 1,
          })
        );
      }
      if ((config?.variant || []).length > 0) {
        const tempVariants: any[] = [];
        config?.variant.forEach((item) => {
          Object.entries(item).forEach(([key, value]: any) => {
            if (allVariants?.[key]) {
              finalProduct.variationsPrice += allVariants?.[key].price;
              tempVariants.push({ ...allVariants[key], isSubLevel: 0 });
            }
            Array.from(value).forEach((subItem: any) => {
              if (allVariants?.[subItem]) {
                finalProduct.variationsPrice += allVariants[subItem].price;
                tempVariants.push({ ...allVariants?.[subItem], isSubLevel: 1 });
              }
            });
          });
        });

        finalProduct.selectedItemsLevel = tempVariants;
        finalProduct.totalUnitPrice += finalProduct.variationsPrice;
      }
      if (
        selectedProduct &&
        !!selectedProduct.isPizza &&
        !!selectedProduct.isDeal &&
        !selectedProduct.isCreateYourOwn
      ) {
        if (!pizza) {
          toast.error("Pizza item is required");
          return;
        }
      }
      if (selectedProduct.isCreateYourOwn || selectedProduct.isPizza) {
        const newPrice = +(getSizePrice(size) || "0");
        if (newPrice) {
          finalProduct.pizzaSize.price = newPrice;
          finalProduct.price = newPrice;
        }
      }
      finalProduct.itemTotal +=
        finalProduct.price + finalProduct.variationsPrice;
      finalProduct.subTotal = finalProduct.itemTotal * config.qty;
      finalProduct.totalUnitPrice = finalProduct.itemTotal;
      if (selectedProduct.taxId1 === 1 && tax1?.rate) {
        const total = +((finalProduct.subTotal * tax1?.rate) / 100).toFixed(5);
        finalProduct.totalTax1 = total;
        finalProduct.taxRate1 = tax1?.rate ?? 0;
        finalProduct.itemTotal += total;
      } else {
        finalProduct.totalTax1 = 0;
        finalProduct.taxRate1 = 0;
      }
      if (selectedProduct.taxId2 === 2 && tax2?.rate) {
        const total = +((finalProduct.subTotal * tax2?.rate) / 100).toFixed(5);
        finalProduct.totalTax2 = total;
        finalProduct.taxRate2 = tax2?.rate ?? 0;
        finalProduct.itemTotal += total;
      } else {
        finalProduct.totalTax2 = 0;
        finalProduct.taxRate2 = 0;
      }
      if (selectedProduct.taxId3 === 3 && tax3?.rate) {
        const total = +((finalProduct.subTotal * tax3.rate) / 100).toFixed(5);
        finalProduct.totalTax3 = total;
        finalProduct.taxRate3 = tax3?.rate ?? 0;
        finalProduct.itemTotal += total;
      } else {
        finalProduct.totalTax3 = 0;
        finalProduct.taxRate3 = 0;
      }
    }

    dispatch(addUpdateToCart(finalProduct, onClose));
  };

  const onClose = () => {
    setConfig(initialConfig);
    setCurrentStep(0);
    setCurrentPizzaIndex(0);
    setPizzasInDeal([]);
    dispatch(setSelected(undefined));
    setCheckCartConfigData([]);
    // === ADDED BY SAMPATH – 2026-01-05 ===
    // Close image preview when closing product popup
    setImagePreviewOpen(false);
    // === END SAMPATH ===
  };

  const moveToNextStep = (value?: any) => {
    // === ADDED BY SAMPATH – 2026-01-05 ===
    // FIX: Replace structuredClone(config) to support ALL devices + prevent 2nd edit crash
    const newConfig: any = safeClone(config);
    if (!Array.isArray(newConfig.variant)) newConfig.variant = [];
    // === END SAMPATH ===

    newConfig.variant[currentStep] = value || selectedVariantItem;
    setConfig(newConfig);

    if (product?.data) {
      setSelectedVariantItem(
        product.data.config?.variant?.[currentStep + 1] || {}
      );
    } else {
      const nextVariants: any = {};
      const nextVariantData = variants?.[currentStep + 1];
      if (nextVariantData) {
        if (
          nextVariantData?.mandatoryLevel === 0 &&
          nextVariantData?.data?.length === 1
        ) {
          nextVariants[nextVariantData?.data[0].id] = new Set();
        }
        setSelectedVariantItem(nextVariants);
      } else {
        setSelectedVariantItem({});
      }
    }
    setCurrentStep((e) => e + 1);
  };

  const getSizePrice = useCallback(
    (item: any) => {
      if (selectedProduct?.isCreateYourOwn) {
        if (!totalModifications) return item?.toppingPrice1 || "";
        if (item?.[`toppingPrice${totalModifications}`])
          return item[`toppingPrice${totalModifications}`];
        const [, basePrice] = Object.entries(item || {}).reduce(
          ([tk, tv], [key, value]: any) =>
            (key.includes("toppingPrice") && value) > tv
              ? [+key.replace("toppingPrice", ""), value]
              : [tk, tv],
          [0, 0]
        );
        return basePrice;
      }

      if (selectedProduct?.isPizza) return item?.sizePriceX1;
    },
    [selectedProduct, totalModifications]
  );

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // UI price: update footer price when Qty changes (and when variants/crust/size changes)
  const uiTotalPrice = useMemo(() => {
    if (!selectedProduct) return 0;

    let basePrice =
      settings.ApplyTakeOutDeliveryPrice !== 0
        ? deliveryType !== "pickup"
          ? selectedProduct?.deliveryprice || selectedProduct.price
          : selectedProduct?.takeoutprice || selectedProduct.price
        : !!selectedProduct.isDeal || !!selectedProduct.isCreateYourOwn
        ? 0
        : selectedProduct.price;

    if (selectedProduct.isPizza || selectedProduct.isCreateYourOwn) {
      const sizeObj = sizes.find((i) => i.sizeId === config.size);
      const sizePrice = Number(getSizePrice(sizeObj) || 0);
      if (!Number.isNaN(sizePrice) && sizePrice > 0) {
        basePrice = sizePrice;
      }
    }

    let unit = Number(basePrice || 0);

    const crustObj = crusts.find((i) => i.modifierId === config.crust);
    if (crustObj?.modifierCost) unit += Number(crustObj.modifierCost || 0);

    if (!selectedProduct.isPizza && Array.isArray(config.variant)) {
      let vTotal = 0;

      config.variant.forEach((item: any) => {
        Object.entries(item || {}).forEach(([key, value]: any) => {
          if (allVariants?.[key]) vTotal += Number(allVariants[key].price || 0);
          Array.from(value || []).forEach((subId: any) => {
            if (allVariants?.[subId])
              vTotal += Number(allVariants[subId].price || 0);
          });
        });
      });

      unit += vTotal;
    }

    const qty = Math.max(1, Number(config.qty || 1));
    return unit * qty;
  }, [
    selectedProduct,
    settings,
    deliveryType,
    sizes,
    crusts,
    config.size,
    config.crust,
    config.qty,
    config.variant,
    allVariants,
    getSizePrice,
  ]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // One-page (NO STEPS): keep ALL variant selections on the same page.
  // We store each level selection in config.variant[levelIndex].
  const setVariantAt = (levelIndex: number, value: any) => {
    setConfig((prev: any) => {
      const next = safeClone(prev);
      if (!Array.isArray(next.variant)) next.variant = [];
      next.variant[levelIndex] = value || {};
      return next;
    });
  };

  const getVariantAt = (levelIndex: number) => {
    const v = (config as any)?.variant?.[levelIndex];
    return v && typeof v === "object" ? v : {};
  };

  const isVariantLevelValid = (levelIndex: number) => {
    const level = variants?.[levelIndex];
    if (!level) return true;

    const selectedObj = getVariantAt(levelIndex) || {};
    const count = Object.keys(selectedObj).length;

    const min = Number(level?.mandatoryLevel || 0);
    const max = Number(level?.qtyLevel || 0);

    // If max not provided (0), treat as no upper limit (but keep min)
    const maxOk = max > 0 ? count <= max : true;
    return count >= min && maxOk;
  };

  const areAllVariantLevelsValid = useMemo(() => {
    if (!selectedProduct) return true;
    if (selectedProduct.isPizza) return true;
    if (!Array.isArray(variants) || variants.length === 0) return true;
    return variants.every((_: any, idx: number) => isVariantLevelValid(idx));
  }, [variants, config.variant, selectedProduct]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // Theme-based colors for footer controls (transparent mix from theme primary).
  const themePrimaryBg = "rgb(var(--color-primary) / 0.12)";
  const themePrimaryBgHover = "rgb(var(--color-primary) / 0.18)";
  const themePrimarySolid = "rgb(var(--color-primary) / 1)";
  // === END SAMPATH ===

  return (
    <Dialog
      onClose={onClose}
      open={!!selectedProduct}
      maxWidth="sm"
      fullWidth
      className="select-none"
      // === ADDED BY SAMPATH – 2026-01-05 ===
      // Popup Corner little bit Round
      PaperProps={{
        className: "rounded-xl overflow-hidden max-h-[92vh] bg-white shadow-2xl",
      }}
      // === END SAMPATH ===
    >
      {selectedProduct && (
        <div className="flex flex-col max-h-[92vh]">
          <div className="relative">
            <img
              src={selectedProduct?.imageUrl || noImage}
              className="w-full h-[180px] object-cover cursor-zoom-in"
              alt="product"
              // === ADDED BY SAMPATH – 2026-01-05 ===
              // Click image -> show image preview popup
              onClick={() => setImagePreviewOpen(true)}
              // === END SAMPATH ===
            />

            <button
              onClick={onClose}
              className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white shadow flex items-center justify-center hover:bg-gray-100 transition"
              aria-label="Close"
              type="button"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
            <div>
              <h4 className="font-bold text-[20px] leading-6">
                {selectedProduct.productName}
              </h4>
              <p className="text-[14px] text-gray-700 mt-1">
                {formatMoney(selectedProduct.price)}
              </p>
              <p className="text-[12px] text-gray-500 mt-2 capitalize">
                {selectedProduct.productDescription.toLowerCase()}
              </p>
            </div>

            <div className="text-[13px] font-semibold text-gray-800">
              Customize your item
            </div>

            {/* === ADDED BY SAMPATH – 2026-01-12 === */}
            {/* REMOVE STEPS UI: show ALL variant levels in ONE PAGE (scroll) */}
            {!selectedProduct.isPizza && !!variants.length && (
              <div className="flex flex-col gap-3">
                {variants.map((level: any, idx: number) => {
                  const selectedForLevel = getVariantAt(idx);
                  const invalid = !isVariantLevelValid(idx);

                  return (
                    <div key={idx} className="rounded-xl bg-gray-50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[13px] font-semibold text-gray-800">
                          {level?.levelName ||
                            level?.name ||
                            level?.title ||
                            `Option ${idx + 1}`}
                        </div>

                        <div className="text-[11px] text-gray-500">
                          {Number(level?.mandatoryLevel || 0) > 0
                            ? "Required"
                            : "Optional"}
                        </div>
                      </div>

                      {invalid && (
                        <p className="text-red-500 text-[12px] leading-4 mb-2">
                          Select Maximum : {level?.qtyLevel} and Minimum :{" "}
                          {level?.mandatoryLevel}
                        </p>
                      )}

                      <VariantSelection
                        selected={selectedForLevel}
                        setSelected={(val: any) => setVariantAt(idx, val)}
                        // keep prop but no step navigation needed
                        moveToNextStep={() => {}}
                        option={level}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {/* === END SAMPATH === */}

            {/* Final stage inputs (NOW ALWAYS VISIBLE like one-page format) */}
            <TextareaAutosize
              // === ADDED BY SAMPATH – 2026-01-12 ===
              // FIX: Note height must be STATIC 60px on ALL devices (no auto-grow).
              // Use fixed height + disable resize + lock rows.
              className="w-full border border-gray-200 p-3 rounded-xl outline-none focus:border-black transition text-[14px] sm:text-[15px] h-[100px] min-h-[100px] max-h-[100px] resize-none overflow-y-auto"
              minRows={2}
              maxRows={2}
              // === END SAMPATH ===
              placeholder="Note..."
              value={config.note}
              onChange={(e) =>
                setConfig((v) => ({ ...v, note: e.target.value }))
              }
            />

            {/* === ADDED BY SAMPATH – 2026-01-12 === */}
            {/* Hide old Quantity block (we move Qty controls to the footer next to Add to Cart) */}
            <div className="hidden">
              <div className="rounded-xl bg-gray-50 p-3 flex items-center justify-between">
                <div className="text-[13px] font-semibold text-gray-800">
                  Quantity
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    className="!px-2 !min-w-0 !rounded-full !bg-white !shadow-sm !border !border-gray-200"
                    onClick={() =>
                      config.qty > 1 &&
                      setConfig((v) => ({ ...v, qty: v.qty - 1 }))
                    }
                  >
                    <RemoveIcon className="!text-gray-800" />
                  </Button>

                  <Input
                    size="small"
                    className="max-w-[70px]"
                    inputProps={{ style: { textAlign: "center" } }}
                    value={config.qty}
                    onValueChange={(value) => {
                      let qty = +value;
                      if (isNaN(qty) || qty < 1) qty = 1;
                      if (qty > 9999) qty = 9999;
                      setConfig((v) => ({ ...v, qty }));
                    }}
                  />

                  <Button
                    className="!px-2 !min-w-0 !rounded-full !bg-white !shadow-sm !border !border-gray-200"
                    onClick={() =>
                      setConfig((v) => ({
                        ...v,
                        qty: v.qty < 9999 ? v.qty + 1 : v.qty,
                      }))
                    }
                  >
                    <AddIcon className="!text-gray-800" />
                  </Button>
                </div>
              </div>
            </div>
            {/* === END SAMPATH === */}
          </div>

          {/* === ADDED BY SAMPATH – 2026-01-12 === */}
          {/* Add to cart + Qty (+/-) in ONE LINE like your image (theme colors + transparent mix) */}
          <div className="sticky bottom-0 bg-white p-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              {/* Qty control (compact pill) */}
              <div
                className="flex items-center rounded-xl border border-black/10 overflow-hidden"
                style={{ backgroundColor: themePrimaryBg }}
              >
                <button
                  type="button"
                  className="h-11 w-11 flex items-center justify-center transition"
                  style={{ backgroundColor: "transparent" }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as any).style.backgroundColor =
                      themePrimaryBgHover)
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as any).style.backgroundColor =
                      "transparent")
                  }
                  onClick={() =>
                    config.qty > 1 && setConfig((v) => ({ ...v, qty: v.qty - 1 }))
                  }
                  aria-label="Decrease quantity"
                >
                  <RemoveIcon
                    className="!text-[20px]"
                    style={{ color: themePrimarySolid }}
                  />
                </button>

                <div className="min-w-[44px] text-center font-semibold text-[14px] text-black select-none">
                  {Math.max(1, Number(config.qty || 1))}
                </div>

                <button
                  type="button"
                  className="h-11 w-11 flex items-center justify-center transition"
                  style={{ backgroundColor: "transparent" }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as any).style.backgroundColor =
                      themePrimaryBgHover)
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as any).style.backgroundColor =
                      "transparent")
                  }
                  onClick={() =>
                    setConfig((v) => ({
                      ...v,
                      qty: v.qty < 9999 ? v.qty + 1 : v.qty,
                    }))
                  }
                  aria-label="Increase quantity"
                >
                  <AddIcon
                    className="!text-[20px]"
                    style={{ color: themePrimarySolid }}
                  />
                </button>
              </div>

              {/* Add to Cart button (same row) */}
              <Button
                disabled={!areAllVariantLevelsValid}
                className="flex-1 min-h-[44px] !rounded-xl flex items-center justify-between px-4"
                onClick={() => {
                  addItemToCart();
                }}
              >
                <span className="flex items-center gap-2">
                  {cartBtnLoading && (
                    <CircularProgress size={20} className="!text-themeSelColor" />
                  )}
                  Add to Cart
                </span>
                <span className="font-semibold">{formatMoney(uiTotalPrice)}</span>
              </Button>
            </div>

            {!areAllVariantLevelsValid && !selectedProduct?.isPizza && (
              <p className="text-[12px] text-red-500 mt-2 text-center">
                Please complete required selections above.
              </p>
            )}
          </div>
          {/* === END SAMPATH === */}
        </div>
      )}

    {/* === ADDED BY SAMPATH – 2026-01-05 === */}
{/* Image preview popup */}
<Dialog
  open={imagePreviewOpen}
  onClose={() => setImagePreviewOpen(false)}
  maxWidth="md"
  fullWidth

  // === ADDED BY SAMPATH – 2026-01-13 ===
  BackdropProps={{
    style: {
      backdropFilter: "blur(12px)",
      backgroundColor: "rgba(0,0,0,0.75)",
    },
  }}
  // === END SAMPATH ===

  PaperProps={{
    className: "rounded-2xl overflow-hidden bg-black/90",
  }}
>
  <div className="relative">
    <button
      onClick={() => setImagePreviewOpen(false)}
      className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white shadow flex items-center justify-center hover:bg-gray-100 transition z-10"
      aria-label="Close image"
      type="button"
    >
      ✕
    </button>

    <img
      src={selectedProduct?.imageUrl || noImage}
      alt="preview"
      className="w-full max-h-[80vh] object-contain bg-black"
    />
  </div>
</Dialog>
{/* === END SAMPATH === */}


      <ModificationPopUp
        open={showModification}
        setOpen={setshowModification}
        onSubmit={(modifications) => {
          setshowModification(undefined);
          setConfig((v) => {
            const toppingTotal = Object.values(modifications).filter(
              (fil: any) => fil.length > 0
            );

            const updatedPizzas = [...pizzasInDeal];
            updatedPizzas[currentPizzaIndex] = !!toppingTotal.length
              ? { ...v, modifications }
              : { ...v };
            setPizzasInDeal(updatedPizzas);

            return {
              ...v,
              modifications: !!toppingTotal.length ? modifications : undefined,
            };
          });
        }}
      />
    </Dialog>
  );
};

export default ProductPopUp;